import React, { useState, useEffect, useMemo, Suspense, useRef } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  useMediaQuery,
  useTheme,
  Grid,
  Skeleton,
  CircularProgress,
  Divider,
  StepConnector,
  styled,
  stepConnectorClasses,
  StepIconProps,
  ButtonBase,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  nextStep,
  resetStepper,
} from "../../services/redux/slices/stepperSlice";
import { AppDispatch, RootState } from "../../services/redux/store";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChatIcon from "../../assets/svgIcons/message-circle-2.svg";
import UploadIcon from "../../assets/svgIcons/upload.svg";
import ProvisionalOfferIcon from "../../assets/icons/mail.png";
import TipsIcon from "../../assets/svgIcons/bulb.svg";
import TipsIconMobile from "../../assets/svgIcons/tips-icon-pwa.svg";
import { OutlinedPrimaryButton, IconButton } from "../../components/ui/Button";
import VerticalLinearStepper from "./VerticalLinearStepper";
import {
  prevVerticalStep,
  setActiveHorizontalIndex,
  setActiveVerticalIndex,
  setDisplayHorizontalSteps,
  setHorizontalSteps,
  setTrackingId,
  setIsPartnerPreEligibilityFlow,
  setPartnerSkipEligibilityTrackingId,
  setIsBypassLogin,
  setIsPreLogin,
  setIsEligibilityCheckSubmitted,
  setLeadSequenceId,
  updateFormData,
  setIsEmailVerified,
  setIsMobileVerified,
  setLastVerifiedEmail,
  setLastVerifiedPhoneNumber,
} from "../../services/redux/slices/stepperDynamicFormSlice";
import {
  getCombinedApplicationSteps,
} from "../../components/ui/StepperDyanmicForm/helper";

import {
  getFormJson,
  fetchProducts,
  recalculateHorizontalSteps,
} from "../../services/redux/thunks/stepperDynamicFormThunks";
import {
  authSelector,
  authStatusSelector,
  setUserAuth,
  setUserProfileData,
  setUserPermission,
  setUserBusinessType,
  setUserLoanCategory,
  setGuestToken,
} from "../../services/redux/slices/authSlice";
import { setDeviceType } from "../../services/redux/slices/deviceTypeSlice";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ProvisionalOfferPage from "../provisional-offer/ProvisionalOfferPage";
import ViewSummary from "../summary/ViewSummary";
import { Check } from "@mui/icons-material";
import TipsBox from "../../components/common/TipsBox";
import ConnectChatComponent from "../../components/ui/ConnectChatComponent";
import { useTriggerData } from "../../hooks/useTriggerData";
import { useNavigate } from "react-router-dom";
import { setStatusPopup } from "../../services/redux/slices/statusPopupSlice";
import chatServices from "../../services/api/service/chatService";
import { chatStatusConfig, setChatStatusConfig } from "../../services/redux/slices/chatSlice";
import { fetchUserApplicationsFormData, fetchPartnerApplication } from "../../services/api/service/userApplicationService";
import { trackCustomerJourneyEvent } from "../../utils/ga";
import { decrypt, encrypt } from "../../utils/crypto";
import axiosInstance from "../../services/api/interceptor/interceptor";
import { isDeviceTypeMobile } from "../../utils/helpers";
// ======================================================
// TYPE DEFINITIONS
// ======================================================
interface HorizontalLinearStepperProps {
  status: boolean;
}
interface StepContent {
  label: string;
  content: React.ReactNode;
}
type HorizontalStep = {
  label: string;
  isCompleted: boolean;
};

type ActiveStep = {
  index: number;
  label: string;
};
// ======================================================
// STYLES
// ======================================================
const CustomConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    borderColor: "#00B3B5",
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    borderColor: "#00B3B5",
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderTopWidth: 4,
    borderRadius: "4px",
    borderColor: "#4B465C14",
    width: "24px",
    margin: "4px",
    flex: 1,
  },
}));

// ✅ Custom Step Icon (FIX)
const CustomStepIcon: React.FC<StepIconProps> = (props) => {
  const { icon, active, completed } = props;

  return (
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: "6px",
        bgcolor: active || completed ? "#00b3b5" : "#e0e0e0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "bold",
        fontSize: "0.9rem",
        color: "#fff",
      }}
    >
      {completed ? <Check sx={{ color: "#fff" }} /> : icon}
    </Box>
  );
};

let tipsArr = [
  {
    formTitle: 'Course Details',
    horizontalIndex: 0,
    verticalIndex: 1,
    heading:"",
    subheading:"",
    tip1: 'Match University and Course names exactly to your acceptance letter to avoid processing delays.',
    tip2: ''
  },
  {
    formTitle: 'Loan Details',
    horizontalIndex: 0,
    verticalIndex: 2,
    heading:"",
    subheading:"",
    tip1: 'Include living costs, travel, and books in your loan amount, not just tuition fee.',
    tip2: ''
  },
  {
    formTitle: 'Student Details',
    horizontalIndex: 1,
    verticalIndex: 0,
    heading:"",
    subheading:"",
    tip1: 'Ensure your name matches your PAN card exactly. Use DigiLocker for the fastest verification.',
    tip2: 'Keep your 6-digit DigiLocker PIN and linked mobile number ready for instant document fetching.'
  },
  {
    formTitle: 'Address Details',
    horizontalIndex: 1,
    verticalIndex: 1,
    heading:"",
    subheading:"",
    tip1: 'Verify your auto-filled Aadhaar address. Edit it manually if you’ve moved recently.',
    tip2: 'Ensure your address is consistent across all documents to speed up your application.'
  },
  {
    formTitle: 'Employment Details',
    horizontalIndex: 1,
    verticalIndex: 2,
    heading:"",
    subheading:"",
    tip1: 'Enter details exactly as shown on your salary slips or IT returns to avoid manual review.',
    tip2: `Include your total work experience across all previous organizations to help us verify your professional profile faster.`
  },
  {
    formTitle: 'Academic Details',
    horizontalIndex: 1,
    verticalIndex: 3,
    heading:"",
    subheading:"",
    tip1: `Convert GPA to percentage using your school's official formula for consistent reporting.`,
    tip2: `Don't wait for final scores! Enter your expected completion date to submit your application now.`
  },
  {
    formTitle: 'Entrance Exam Details',
    horizontalIndex: 1,
    verticalIndex: 4,
    heading:"",
    subheading:"",
    tip1: 'Enter your total composite score (GMAT/GRE) rather than individual section scores.',
    tip2: 'Ensure exam dates and scores match your certificate to verify your offer quickly.'
  },
  {
    formTitle: 'Loan Details',
    horizontalIndex: 1,
    verticalIndex: 5,
    heading:"",
    subheading:"",
    tip1: 'Use current market value for property/security, not the original purchase price.',
    tip2: 'Co-applicants must be close relatives with steady income and an active email for digital signing.'
  },
  {
    formTitle: 'Additional Details & Documents',
    horizontalIndex: 3,
    verticalIndex: 0,
    heading:"Let’s Customise Your Education Loan",
    subheading:"Let’s build a loan plan that fits your study goals! Complete the following sections:",
    tip1: 'Applicant Details: Your background info and basic details.',
    tip2: 'Loan Details: Your required loan amount, tenure,  interest rate, and more.',
  },
  {
    formTitle: 'Additional Details & Documents',
    horizontalIndex: 3,
    verticalIndex: 1,
    heading:"Co-applicant Checklist (2 Required)",
    subheading:"Keep these handy for both co-applicants so you can finish in minutes:",
    tip1: 'PAN details for instant identity verification.',
    tip2: 'DigiLocker Access for quick, paperless Aadhaar verification.',
    tip3: ' Income & Job Details to confirm employment status.',
  },
  {
    formTitle: 'Additional Details & Documents',
    horizontalIndex: 3,
    verticalIndex: 2,
    heading:"Your Financial Info = A Loan Plan Matched To Your Exact Needs",
    subheading:"Share your income and expense details to unlock customised loan terms and fast-track the process.",
    tip1: '',
    tip2: ''
  },
  {
    formTitle: 'Additional Details & Documents',
    horizontalIndex: 3,
    verticalIndex: 3,
    heading:"Power Up Your Loan Profile: References & Collateral",
    subheading:"",
    tip1: 'Personal References: 2 trusted contacts who can vouch for you.',
    tip2: 'Collateral Details (for a secured student loan): Info on the asset used as loan security.',
  },
  {
    formTitle: 'Additional Details & Documents',
    horizontalIndex: 3,
    verticalIndex: 4,
    heading:"Complete Document Verification and Provide Consent",
    subheading:"",
    tip1: 'Upload documents: Use DigiLocker or Account Aggregator for instant document retrieval, or upload files manually.',
    tip2: 'Verify & consent: Verify your mobile no. & email ID to give consent and move forward with your loan application.',
  },
];

// Maps a partner-application-fetch response's lead data onto the same field
// names dynamic-form.json uses for "Provide Your Course Details"
// (courseCategory, admissionStatus, country, university, institute,
// courseName, courseType) and "Provide Loan Details" (loanAmount,
// totalCost). `application` is one entry of partner-application-fetch's
// `applications` array — same horizontalSteps[].verticalSteps[].fields[]
// shape (each field a single {fieldName: value} object) as
// fetchUserApplicationsFormData's response, already handled the same way
// elsewhere in this codebase (see helper.ts's isApplicationCompleted). Every
// field across every step is flattened into one flat map — field names are
// unique across the whole application, and passing through a few extra
// unrelated ones (e.g. "Verify Yourself" fields) via updateFormData is
// harmless since they're just sitting unused until the applicant would
// otherwise reach that step.
const mapPartnerLeadDataToFormValues = (application: any): Record<string, any> => {
  const values: Record<string, any> = {};
  const horizontalSteps = Array.isArray(application?.horizontalSteps)
    ? application.horizontalSteps
    : [];
  horizontalSteps.forEach((hStep: any) => {
    (hStep?.verticalSteps || []).forEach((vStep: any) => {
      (vStep?.fields || []).forEach((fieldObj: any) => {
        if (fieldObj && typeof fieldObj === "object") {
          Object.assign(values, fieldObj);
        }
      });
    });
  });
  return values;
};

// Resolves where an "eligibiltylead" leadType should land, purely from the
// shape of partner-application-fetch's own response (no separate schema
// fetch needed):
// - horizontalSteps' array order already matches activeHorizontalIndex 1:1
//   — both follow the same fixed 6-group order (Eligibility Check,
//   Applicant & Loan Details, Provisional Offer Letter, Additional Details
//   & Documents, Final Verification & Sanction, Disbursement of Funds; see
//   STEP_NAME in StepperDyanmicForm/helper.ts), and the response always
//   includes all 6 entries (even empty ones), so array index === group
//   index.
// - Vertical index is derived from verticalStepId (globally sequential
//   across the whole application, confirmed against the sample response —
//   e.g. Eligibility Check's 3 substeps are ids 1-3, Applicant & Loan
//   Details' substeps start at id 4) minus the cumulative
//   verticalStepsCount of every earlier horizontal group. This is robust
//   even though this response's own verticalSteps arrays are sparse (only
//   lists steps that actually have field data, so array position within
//   them can't be used directly).
const resolveStageBasedStep = (
  application: any,
): { horizontalIndex: number; verticalIndex: number } => {
  const horizontalSteps: any[] = Array.isArray(application?.horizontalSteps)
    ? application.horizontalSteps
    : [];

  const allFilled =
    horizontalSteps.length > 0 &&
    horizontalSteps.every((h: any) => Number(h?.completionRate) === 100);
  if (allFilled) {
    // "third step" (1-indexed, per spec) = Provisional Offer Letter
    return { horizontalIndex: 2, verticalIndex: 0 };
  }

  let cumulativeVerticalCount = 0;
  let lastFilled: { horizontalIndex: number; verticalIndex: number } | null =
    null;
  horizontalSteps.forEach((hStep: any, hIdx: number) => {
    const verticalSteps = Array.isArray(hStep?.verticalSteps)
      ? hStep.verticalSteps
      : [];
    verticalSteps.forEach((vStep: any) => {
      if (Array.isArray(vStep?.fields) && vStep.fields.length > 0) {
        const verticalIndex =
          Number(vStep?.verticalStepId || 0) - 1 - cumulativeVerticalCount;
        if (verticalIndex >= 0) {
          lastFilled = { horizontalIndex: hIdx, verticalIndex };
        }
      }
    });
    cumulativeVerticalCount += Number(hStep?.verticalStepsCount) || 0;
  });

  return lastFilled || { horizontalIndex: 0, verticalIndex: 0 };
};

// ======================================================
// COMPONENT
// ======================================================
const HorizontalLinearStepper: React.FC<
  HorizontalLinearStepperProps
> = ({ }) => {
  const chatRef = useRef<HTMLDivElement>(null);
  const connectButtonRef = useRef<HTMLButtonElement>(null);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const {
    horizontalSteps,
    displayHorizontalSteps,
    activeHorizontalIndex,
    activeVerticalIndex,
    selectedProduct,
    loading,
    trackingId,
  } = useSelector((state: RootState) => state.stepper);
  const { userProfile } = useSelector(authSelector);
  const newactiveVerticalIndex = useSelector(
    (state: RootState) =>
      state.stepper.activeVerticalIndex[activeHorizontalIndex] || 0
  );
  const revisedActiveVerticalIndex = activeVerticalIndex[1];
  const isPreviousButton = process.env.REACT_APP_ENABLE_PREVIOUS_BUTTON == "true";
  const provisionalOffer = useSelector(
    (state: RootState) => state.stepper.breData
  );
  const { chatAccess, viewAccess } = useSelector(chatStatusConfig);
  const salesUserId = useSelector((state: RootState) => state.chat.salesUserId);
  const isAuthenticated = useSelector(authStatusSelector);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const shouldHideChatForPwa = isXsOrSm;
  const isIphoneSE = useMediaQuery(`(max-width: 400px)`);
  const [isChatConnectOpen, setIsChatConnectOpen] = useState(false);
  const [isPageScrollable, setIsPageScrollable] = useState(false);
  const [isFetchingSteps, setIsFetchingSteps] = useState(true);
  const [ActivedisplayHorizontalSteps, setActivedisplayHorizontalSteps] = useState(displayHorizontalSteps);
  const [activeHorizontalStep, setActiveHorizontalStep] = useState({
    index: 0,
    label: "",
  });
  const [isStepperDataReady, setIsStepperDataReady] = useState(false);
  const { isTriggerDataEmpty, triggerData }: any = useTriggerData();
  const isApplicant = isTriggerDataEmpty ? true : triggerData?.toUser;

  // Partner links carry a `t` query param — decrypted, it's the
  // applicationId (used as-is as trackingId going forward, confirmed same
  // value). Fetching it seeds this application's lead data and, when the
  // response's isPreEligibility flag is true, lands the applicant on Course
  // Details instead of today's default start — DynamicForm's Loan Details
  // submit handler reads isPartnerPreEligibilityFlow from redux to also
  // skip the BRE call at that point. Flag false/missing (or no `t` param
  // at all): nothing here changes routing, so the existing flow is
  // untouched.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("t");
    if (!t) return;

    (async () => {
      try {
        const decryptedT = decrypt(decodeURIComponent(t));
        if (!decryptedT) return;

        // New links decrypt to JSON { trackingId, token: "Bearer ..." };
        // older links decrypt to the bare applicationId.
        let applicationId = decryptedT;
        let partnerToken: string | undefined;
        try {
          const parsedT = JSON.parse(decryptedT);
          console.log(parsedT, "parsedT");
          if (parsedT && typeof parsedT === "object") {
            applicationId = String(parsedT?.trackingId || "");
            partnerToken = parsedT?.token || undefined;
          }
        } catch {
          // Not JSON — legacy plain applicationId.
        }
        if (!applicationId) return;
        if (partnerToken && !/^Bearer\s/i.test(partnerToken)) {
          partnerToken = `Bearer ${partnerToken}`;
        }
        // Send the partner token as Authorization on this and every following
        // call until a new token arrives (bypass-login / x-new-token →
        // setUserAuth clears the guest token).
        if (partnerToken) {
          dispatch(setGuestToken(partnerToken));
        }

        const resp: any = await fetchPartnerApplication(
          applicationId,
          partnerToken,
        );
        // Real shape: { status, message, applications: [{...}] } — same
        // "applications[0]" shape fetchUserApplicationsFormData already
        // returns elsewhere in this app, not a data.data envelope.
        const application = resp?.data?.applications?.[0];
        if (!application) return;

        dispatch(setTrackingId(applicationId));
        dispatch(
          setIsPartnerPreEligibilityFlow(Boolean(application?.isPreEligibility)),
        );
        // skipEligibility is only honored for an eligibility lead opened via the
        // apply-now?t=... partner link. It's stored as this application's
        // trackingId, so DynamicForm skips check-eligibility (BRE) only for this
        // exact application — every other flow/application is untouched.
        const partnerLeadType = String(application?.leadType || "")
          .trim()
          .toLowerCase();
        const isEligibilityLead =
          partnerLeadType === "eligibiltylead" ||
          partnerLeadType === "eligibilitylead";
        const isApplyNowRoute = /\/apply-now\/?$/.test(window.location.pathname);
        const shouldSkipEligibility =
          isApplyNowRoute &&
          isEligibilityLead &&
          String(application?.skipEligibility) === "true";
        dispatch(
          setPartnerSkipEligibilityTrackingId(
            shouldSkipEligibility ? String(applicationId) : "",
          ),
        );
        const partnerFormValues = mapPartnerLeadDataToFormValues(application);
        dispatch(updateFormData(partnerFormValues));
        // DynamicForm copies the redux isEmailVerified / isMobileVerified flags
        // into Formik, and the email field's Yup test requires
        // isEmailVerified === true. The dashboard flows seed these from the
        // fetched application; this partner link never did, so an already
        // verified email still showed "Email verification is pending".
        // Use the fetched flag when present, otherwise treat a fetched value
        // as verified (same fallback the dashboard uses).
        const resolveVerifiedFlag = (flag: any, value: any): boolean => {
          if (typeof flag === "boolean") return flag;
          if (typeof flag === "string" && flag.trim() !== "") {
            return flag.trim().toLowerCase() === "true";
          }
          return Boolean(String(value ?? "").trim());
        };
        const partnerEmail = partnerFormValues?.email;
        const partnerPhone = partnerFormValues?.phone_number;
        const isPartnerEmailVerified = resolveVerifiedFlag(
          partnerFormValues?.isEmailVerified,
          partnerEmail,
        );
        const isPartnerMobileVerified = resolveVerifiedFlag(
          partnerFormValues?.isMobileVerified,
          partnerPhone,
        );
        dispatch(setIsEmailVerified(isPartnerEmailVerified));
        dispatch(setIsMobileVerified(isPartnerMobileVerified));
        if (isPartnerEmailVerified && partnerEmail) {
          dispatch(setLastVerifiedEmail(partnerEmail));
        }
        if (isPartnerMobileVerified && partnerPhone) {
          dispatch(setLastVerifiedPhoneNumber(partnerPhone));
        }

        if (application?.isPreEligibility) {
          dispatch(setActiveHorizontalIndex(0));
          dispatch(setActiveVerticalIndex(1));
        } else {
          // leadType is a separate, independent routing rule from
          // isPreEligibility above (only applied when that one didn't
          // already decide where to land): "quicklead" starts at the very
          // first page (today's default — dispatched explicitly in case a
          // prior session left a non-zero index persisted), "eligibiltylead"
          // resumes at whatever step the response's own data indicates
          // (see resolveStageBasedStep). Any other/missing leadType: no
          // routing change, same as today.
          const leadType = String(application?.leadType || "")
            .trim()
            .toLowerCase();
          if (leadType === "quicklead") {
            dispatch(setActiveHorizontalIndex(0));
            dispatch(setActiveVerticalIndex(0));
          } else if (
            leadType === "eligibiltylead" ||
            leadType === "eligibilitylead"
          ) {
            const target = resolveStageBasedStep(application);
            // Landing on Student Details (horizontalIndex 1) needs an
            // authenticated session — that step (and the isQualified fetch
            // it depends on elsewhere in the app) reads applicant data via
            // fetchUserApplicationsFormData, gated on isAuthenticated. A
            // partner link arrives with no login at all, so establish one
            // unauthenticated Continue click already uses, before
            // navigating there — otherwise the step would land empty.
            if (target.horizontalIndex === 1 && !isAuthenticated) {
              try {
                const mobile = partnerFormValues?.phone_number;
                const encryptedKey = encrypt(mobile);
                const bypassResp: any = await axiosInstance.post(
                  "api/v1/user-lead/guest-login",
                  { key: encryptedKey, role: "applicant" },
                );
                if (bypassResp?.data?.status === "success") {
                  const bypassData: any = JSON.parse(
                    decrypt(bypassResp?.data?.data),
                  );
                  dispatch(setUserProfileData(bypassData?.user));
                  dispatch(setUserPermission(bypassData?.permission));
                  dispatch(
                    setUserBusinessType(
                      bypassData?.businessType?.length > 0
                        ? bypassData?.businessType?.[0]
                        : {},
                    ),
                  );
                  dispatch(setUserLoanCategory(bypassData?.result));
                  dispatch(setUserAuth(bypassData?.token));
                  dispatch(setIsBypassLogin(true));
                  dispatch(setIsPreLogin(true));
                  dispatch(setIsEligibilityCheckSubmitted(true));

                  const fetchToken: any = process.env.REACT_APP_FETCH_TOKEN;
                  const appRes: any = await fetchUserApplicationsFormData(
                    fetchToken,
                    applicationId,
                  );
                  const apps = appRes?.data?.applications || [];
                  const finalSteps = getCombinedApplicationSteps(apps[0]);
                  dispatch(setDisplayHorizontalSteps(finalSteps));
                  dispatch(setLeadSequenceId(apps[0]?.leadId));
                }
              } catch (bypassError) {
                console.error("Bypass login failed", bypassError);
              }
            }
            dispatch(setActiveHorizontalIndex(target.horizontalIndex));
            dispatch(setActiveVerticalIndex(target.verticalIndex));
          }
        }
      } catch (error) {
        console.error("Failed to decrypt/fetch partner application", error);
      }
    })();
  }, [dispatch]);


  // useEffect(() => {
  //   // dispatch(fetchProducts());
  // }, [dispatch]);

  // Sequential: getFormJson → recalculateHorizontalSteps → fetchAPI (always last to win)
  useEffect(() => {
    // if (!isAuthenticated) {
    //   setIsFetchingSteps(false);
    //   return;
    // }

    let isMounted = true;
    //setIsFetchingSteps(true);

    const runSequential = async () => {
      try {
        let payload :any = {
           product: selectedProduct
        };
        if(trackingId){
         payload = { ...payload, trackingId:trackingId };
        }
        // Step 1: Get form JSON (internally calls recalculateHorizontalSteps)
        await dispatch(getFormJson(payload));

        //if (!isMounted) return;

        // Step 2: Fetch API data AFTER recalculation so it always wins
        if (!trackingId) return;

        const token: any = process.env.REACT_APP_FETCH_TOKEN;
        // if (!isMounted) return;
        if (isAuthenticated) {
          const res = await fetchUserApplicationsFormData(token, trackingId);
          const apps = res?.data?.applications ?? [];
          if (apps.length > 0) {
            // console.log(apps[0], "finalSteps");

            const lengthOfHorizontalSteps = apps?.[0]?.horizontalSteps?.length;
            let apiFormData: any = {};
            for (let i = 0; i < lengthOfHorizontalSteps; i++) {
              const verticalSteps =
                apps?.[0]?.horizontalSteps?.[i]?.verticalSteps;
              for (let j = 0; j < verticalSteps?.length; j++) {
                const fields = verticalSteps?.[j]?.fields;
                for (let k = 0; k < fields?.length; k++) {
                  const field = fields?.[k];
                  apiFormData = { ...apiFormData, ...field };
                }
              }
            }

            dispatch(updateFormData(apiFormData)); 
            let finalSteps = getCombinedApplicationSteps(apps[0]);
            if(activeHorizontalIndex === 0) {
              finalSteps[0].isCompleted = false;
            }
            dispatch(setDisplayHorizontalSteps([...finalSteps]));
          } else {
            dispatch(setDisplayHorizontalSteps([]));
          }
        }

      } catch {
        // handle silently
      } finally {
        //if (isMounted) setIsFetchingSteps(false);
      }
    };

    runSequential();

    return () => {
      isMounted = false;
    };
  }, [trackingId, isAuthenticated]);



  useEffect(() => {
    dispatch(resetStepper());
  }, [dispatch]);

  const getChatFeatureStatus = async (appNum: string) => {
    try {
      const res = await chatServices.getChatFeatureStatus({
        applicationId: appNum
      });

      const responseData = res.data?.data;
      if (responseData) {
        dispatch(setChatStatusConfig({
          chatAccess: responseData.chatAccess,
          viewAccess: responseData.viewAccess,
          loading: false
        }));
      }
    } catch (error) {
    }
  };

  useEffect(() => {
    if (trackingId && location.pathname != "/apply-now") {
      getChatFeatureStatus(trackingId);
    }
  }, [trackingId]);

  // Track when displayHorizontalSteps is actually populated with data
  useEffect(() => {
    // Always start with false to prevent flash
    setIsStepperDataReady(false);

    if (displayHorizontalSteps && displayHorizontalSteps.length > 0) {
      // Add a small delay to ensure Redux state has fully updated
      const timer = setTimeout(() => {
        // Check if the data has the required structure (isCompleted, isActive flags)
        const hasValidStructure = displayHorizontalSteps.every(
          (step: any) =>
            step.hasOwnProperty("isCompleted") &&
            step.hasOwnProperty("isActive") &&
            step.hasOwnProperty("label")
        );

        // Also verify that activeHorizontalIndex matches the actual active step
        const activeStepIndex = displayHorizontalSteps.findIndex(
          (step: any) => step.isActive === true
        );
        const isIndexCorrect =
          activeStepIndex === -1 || activeStepIndex === activeHorizontalIndex;

        if (hasValidStructure && isIndexCorrect) {
          setIsStepperDataReady(true);
        } else {
          setIsStepperDataReady(false);
        }
      }, 100);

      return () => clearTimeout(timer);
    } else {
      setIsStepperDataReady(false);
    }
  }, [displayHorizontalSteps, activeHorizontalIndex]);

  useEffect(() => {
    const handlePopState = () => {
      dispatch(setHorizontalSteps([]));
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const horizontalStepsFormSteps: any = useMemo(() => {
    const steps: StepContent[] = [];
    const reversedSteps = [...horizontalSteps].reverse();
    reversedSteps?.forEach((elem: any) => {
      steps.unshift({
        label: elem?.stepLabel,
        content: (
          <>
            {VerticalLinearStepper && (
              <Suspense fallback={<div>Loading step...</div>}>
                <VerticalLinearStepper
                  steps={elem?.verticalSteps}
                  reset={false}
                />
              </Suspense>
            )}
          </>
        ),
      });
    });

    return steps;
  }, [horizontalSteps, VerticalLinearStepper]);

  const showVerticalStepper = useMemo(() => {
    return horizontalStepsFormSteps?.[activeHorizontalIndex]?.content || null;
  }, [horizontalStepsFormSteps, activeHorizontalIndex]);

  const handleNext = () => {
    if (activeHorizontalIndex < horizontalSteps.length - 1) {
      dispatch(nextStep());
    }
  };

  const handleBack = () => {
    dispatch(prevVerticalStep());
  };

  useEffect(() => {
    const handlePopState = () => {
      dispatch(setHorizontalSteps([]));
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  const handleSaveAsDraftAndNext = () => {
    handleNext();
  };

  const SkeletonLoader = () => {
    return (
      <Box
        sx={{
          width: "100%",
          mb: { xs: 2, sm: 2 },
          pt: { xs: 1, sm: 1 },
          px: { xs: 1, sm: 0 },
        }}
      >
        {isXsOrSm ? (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Skeleton variant="text" width="60%" height={32} />
              <Skeleton variant="text" width="30%" height={24} />
            </Box>
            <Box mt={2}>
              {Array.from({ length: 1 }).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="text"
                  height={28}
                  sx={{ my: 1 }}
                />
              ))}
            </Box>
          </>
        ) : (
          <Box
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
            gap={0}
          >
            <Box sx={{ flex: 1, mr: 2 }}>
              <Stepper activeStep={0}>
                {Array.from({ length: 6 }).map((_, index) => (
                  <Step key={index}>
                    <StepLabel>
                      <Skeleton width={100} height={20} />
                      <Skeleton width={120} height={14} />
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const SkeletonVerticalStepper = () => {
    const stepCount = 4;

    return (
      <>
        {/* Header Grid */}
        <Grid container sx={{ borderBottom: "1px solid #dbdade", mb: 2 }}>
          {SkeletonLoader()}
          <Grid item xs={12} md={9}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 1,
                mt: 2,
              }}
            >
              <Skeleton variant="text" width={"100%"} height={58} />
            </Box>
          </Grid>

          <Grid item xs={12} md={3}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1.5,
                textAlign: "right",
                mr: 2,
                mt: 2,
              }}
            >
              <Skeleton variant="text" width={"100%"} height={20} />
              <Box sx={{ position: "relative", display: "inline-flex" }}>
                <CircularProgress
                  variant="determinate"
                  value={80}
                  size={28}
                  thickness={4}
                  sx={{
                    color: "#e0e0e0",
                    position: "absolute",
                    left: 0,
                    top: 0,
                  }}
                />
                <CircularProgress
                  variant="indeterminate"
                  size={28}
                  thickness={4}
                  sx={{
                    color: "#00b3b5",
                  }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Main Layout: Stepper + Form */}
        {isXsOrSm ? (
          <>
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              {/* Right Form Area */}
              <Box sx={{ flex: 1, pl: 2, pr: 2, mb: 2, mt: "5px" }}>
                {/* Product Selection Dropdown */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={48}
                    sx={{
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 2,
                    }}
                  >
                    <Skeleton variant="text" width="40%" height={16} />
                    <Skeleton variant="circular" width={16} height={16} />
                  </Skeleton>
                </Box>

                {/* Applicant Type Radio Buttons */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="70%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ display: "flex", gap: 3, mt: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Skeleton variant="circular" width={20} height={20} />
                      <Skeleton variant="text" width={60} height={16} />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Skeleton variant="circular" width={20} height={20} />
                      <Skeleton variant="text" width={80} height={16} />
                    </Box>
                  </Box>
                </Box>

                {/* Student First Name */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="50%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={48}
                    sx={{ borderRadius: 1 }}
                  />
                </Box>

                {/* Student Last Name */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="50%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={48}
                    sx={{ borderRadius: 1 }}
                  />
                </Box>

                {/* Student Mobile Number */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={48}
                    sx={{ borderRadius: 1 }}
                  />
                </Box>

                {/* Student Email */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="50%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={48}
                    sx={{ borderRadius: 1 }}
                  />
                </Box>
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ display: "flex", flexDirection: "row" }}>
              {/* Vertical Stepper */}
              <Box
                sx={{
                  borderRight: "1px solid #dcdddf",
                  minWidth: 120,
                  width: "140px",
                  display: "flex",
                  padding: "20px",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                {Array.from({ length: stepCount }).map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      mb: 1,
                      ml: 1,
                      minWidth: 92,
                    }}
                  >
                    {/* Step Circle */}
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        backgroundColor: "#fff",
                        border: "3px solid #eee",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                      }}
                    >
                      <Skeleton variant="circular" width={20} height={20} />
                    </Box>

                    {/* Step Label */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        maxWidth: 135,
                      }}
                    >
                      <Skeleton width={70} height={12} sx={{ mb: 1 }} />
                      {index < stepCount - 1 && (
                        <Skeleton
                          variant="rectangular"
                          width={12}
                          height={12}
                        />
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Right Form Area */}
              <Box sx={{ flex: 1, pl: 2, pr: 2, mb: 2, mt: "5px" }}>
                {/* Product Selection Dropdown */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={48}
                    sx={{
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 2,
                    }}
                  >
                    <Skeleton variant="text" width="40%" height={16} />
                    <Skeleton variant="circular" width={16} height={16} />
                  </Skeleton>
                </Box>

                {/* Applicant Type Radio Buttons */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="70%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ display: "flex", gap: 3, mt: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Skeleton variant="circular" width={20} height={20} />
                      <Skeleton variant="text" width={60} height={16} />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Skeleton variant="circular" width={20} height={20} />
                      <Skeleton variant="text" width={80} height={16} />
                    </Box>
                  </Box>
                </Box>

                {/* Student First Name */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="50%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={48}
                    sx={{ borderRadius: 1 }}
                  />
                </Box>

                {/* Student Last Name */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="50%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={48}
                    sx={{ borderRadius: 1 }}
                  />
                </Box>

                {/* Student Mobile Number */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={48}
                    sx={{ borderRadius: 1 }}
                  />
                </Box>

                {/* Student Email */}
                <Box mb={3}>
                  <Skeleton
                    variant="text"
                    width="50%"
                    height={20}
                    sx={{ mb: 1 }}
                  />
                  <Skeleton
                    variant="rectangular"
                    height={48}
                    sx={{ borderRadius: 1 }}
                  />
                </Box>
              </Box>
            </Box>
          </>
        )}
      </>
    );
  };

  const handleShowProvisionalOffer = useMemo(() => {
    return (
      <>
        {isXsOrSm ? (
          <Box sx={{ mt: 2 }}>
            <ProvisionalOfferPage />
          </Box>
        ) : (
          <Box
            sx={{
              background: "#fff",
              borderRadius: 2,
              boxShadow: 1,
              pt: 2,
              maxWidth: "100%",
              mt: 2,
            }}
          >
            {provisionalOffer?.Provisional_Offer &&
              provisionalOffer?.Provisional_Offer !== "No" && (
                <Box sx={{ paddingLeft: "15px", paddingRight: "15px" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: "Jost",
                      fontWeight: 500,
                      fontSize: "16px",
                      lineHeight: "20px",
                      letterSpacing: "0px",
                      verticalAlign: "middle",
                      background:
                        "linear-gradient(0deg, #4B465C, #4B465C), linear-gradient(0deg, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5)",
                      backgroundBlendMode: "overlay", // or "multiply", "screen", etc.
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        width: "100%",
                      }}
                    >
                      <img
                        src={ProvisionalOfferIcon}
                        alt="Provisional Offer"
                        style={{
                          width: "40px",
                          height: "40px",
                          marginRight: "10px",
                        }}
                      />
                      <span
                        style={{
                          fontSize: "22px",
                          fontWeight: 600,
                          color: "rgba(75, 70, 92, 1)",
                          lineHeight: "30px",
                        }}
                      >
                        Provisional Offer
                      </span>
                    </Box>
                  </Typography>
                  <Divider
                    sx={{ color: "rgba(215, 216, 222, 1)", paddingTop: "15px" }}
                  />
                </Box>
              )}

            <ProvisionalOfferPage />
          </Box>
        )}
      </>
    );
  }, [activeHorizontalIndex, provisionalOffer]);

  // chat window open/close functions
  const handleChatConnectOpen = () => {
    if (shouldHideChatForPwa) {
      setIsChatConnectOpen(false);
      return;
    }


    trackCustomerJourneyEvent(
      "CHAT_APP_JOURNEY",
      decrypt(userProfile?.id) ?? "",
      trackingId,
      {
        label: "CHAT_APP_JOURNEY",
        category: "CHAT_APP_JOURNEY",
        action: "CHAT_APP_JOURNEY",
        page: "Chat Option_App Journey",
      }
    );

    if (salesUserId) {
      setIsChatConnectOpen(true);
    }
  };
  const handleChatConnectClose = () => setIsChatConnectOpen(false);

  useEffect(() => {
    if (shouldHideChatForPwa && isChatConnectOpen) {
      setIsChatConnectOpen(false);
    }
  }, [shouldHideChatForPwa, isChatConnectOpen]);

  // close chat window on outside click
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        isChatConnectOpen &&
        chatRef.current &&
        !chatRef.current.contains(event.target) &&
        !connectButtonRef.current?.contains(event.target)
      ) {
        handleChatConnectClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isChatConnectOpen]);

  // prevent body scroll when chat is open
  useEffect(() => {
    if (isChatConnectOpen) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
      document.documentElement.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
      document.documentElement.style.overflow = "unset";
    };
  }, [isChatConnectOpen]);

  // check if the whole document body is scrollable
  const checkPageScrollable = () => {
    const scrollable = document.body.scrollHeight > window.innerHeight;
    setIsPageScrollable(scrollable);
  };

  useEffect(() => {
    checkPageScrollable();
    window.addEventListener("resize", checkPageScrollable);
    const observer = new MutationObserver(checkPageScrollable);
    if (contentContainerRef.current) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
    return () => {
      window.removeEventListener("resize", checkPageScrollable);
      observer.disconnect();
    };
  }, [loading, activeHorizontalIndex]);

  // this function is to get index and label to display current active step in mobile only
  useEffect(() => {
    if (!isXsOrSm || !displayHorizontalSteps?.length) return;

    // Special case: at very first step (horizontal 0, vertical 0), force first step as in-progress
    if (activeHorizontalIndex === 0 && newactiveVerticalIndex === 0) {
      setActiveHorizontalStep({
        index: 1,
        label: displayHorizontalSteps[0]?.label ?? "",
      });
      return;
    }

    const firstIncompleteIndex: number =
      displayHorizontalSteps.findIndex(
        (step: HorizontalStep) => !step.isCompleted
      );

    const index: number =
      firstIncompleteIndex !== -1
        ? firstIncompleteIndex
        : displayHorizontalSteps.length - 1;

    const activeStep: HorizontalStep | undefined =
      displayHorizontalSteps[index];

    setActiveHorizontalStep({
      index: index + 1,
      label: activeStep?.label ?? "",
    });
  }, [displayHorizontalSteps, isXsOrSm, activeHorizontalIndex, newactiveVerticalIndex]);

  useEffect(() => {
    console.log(displayHorizontalSteps, "displayHorizontalStepsggg");
  }, [displayHorizontalSteps]);

  return (
    <Box
      ref={contentContainerRef}
      sx={{
        width: "100%",
        mb: { xs: 2, sm: 2 },
        pt: { xs: 1, sm: 1 },
        px: { xs: 1, sm: 0 },
      }}
    >
      {isApplicant && <></>}

      {loading ? (
        <>{SkeletonVerticalStepper()}</>
      ) : (
        <>
          {/* === Stepper === */}
          {isApplicant && isXsOrSm ? (
            <>
              {/* Mobile Stepper Header */}
              <Box display="flex" justifyContent="space-between" sx={{mt: isDeviceTypeMobile(isXsOrSm) ? 10 : 0}}>
                {isDropdownOpen ? (
                  <Typography></Typography>
                ) : displayHorizontalSteps && displayHorizontalSteps.length > 0 ? (
                  <Typography
                    sx={{
                      fontFamily: "Jost, Arial, sans-serif",
                      fontWeight: 600,
                      fontStyle: "normal",
                      fontSize: isIphoneSE ? "14px" : "16px",
                      letterSpacing: 0,
                      color: "rgba(22, 135, 148, 1)",
                      marginTop: isIphoneSE ? "8px" : "7px",
                    }}
                  >
                    {`${activeHorizontalStep?.index}. `}
                    {activeHorizontalStep?.label}
                  </Typography>
                ) : (
                  <Typography
                    sx={{
                      fontFamily: "Jost, Arial, sans-serif",
                      fontWeight: 600,
                      fontStyle: "normal",
                      fontSize: isIphoneSE ? "14px" : "16px",
                      letterSpacing: 0,
                      color: "rgba(22, 135, 148, 1)",
                      marginTop: isIphoneSE ? "8px" : "7px",
                    }}
                  >
                    {`1. `}{horizontalSteps?.[0]?.stepLabel ?? "Step 1"}
                  </Typography>
                )}

                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    sx={{
                      width: "auto",
                      height: isIphoneSE ? 25 : 28,
                      gap: "10px",
                      borderRadius: "20px",
                      pt: isIphoneSE ? "5px" : "4px",
                      pr: "12px",
                      pb: "4px",
                      pl: "12px",
                      opacity: 1,
                      background:
                        "linear-gradient(0deg, #00CFE8, #00CFE8), linear-gradient(0deg, rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8))",
                      backgroundBlendMode: "overlay",
                      color: "rgba(22, 135, 148, 1)",
                      fontSize: isIphoneSE ? "10px" : "14px",
                      fontWeight: 500,
                    }}
                  >
                    {displayHorizontalSteps && displayHorizontalSteps.length > 0
                      ? `${activeHorizontalStep?.index} of 6`
                      : `1 of 6`}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{ cursor: "pointer", marginTop: "8px" }}
                    onClick={() => setIsDropdownOpen((prev) => !prev)}
                  >
                    {isDropdownOpen ? (
                      <KeyboardArrowUpIcon />
                    ) : (
                      <KeyboardArrowDownIcon />
                    )}
                  </Typography>
                </Box>
              </Box>

              {isDropdownOpen && (
                <Box mt={-5}>
                  {(displayHorizontalSteps && displayHorizontalSteps.length > 0
                    ? displayHorizontalSteps
                    : horizontalSteps?.map((s: any) => ({ label: s.stepLabel, isCompleted: false, isActive: false })) ?? []
                  ).map((step: any, index: any) => {
                    // When using horizontalSteps fallback at (0,0), treat first step as in-progress
                    const isFallback = !displayHorizontalSteps || displayHorizontalSteps.length === 0;
                    const isInProgress = isFallback && index === 0;
                    const isStepActive = isInProgress || index + 1 <= activeHorizontalStep?.index;
                    return (
                      <Typography
                        key={index}
                        sx={{
                          color: isStepActive ? "rgba(22, 135, 148, 1)" : "rgba(75, 70, 92, 1)",
                          cursor: "pointer",
                          fontWeight: isStepActive ? 600 : 400,
                          fontSize: "15px",
                          my: 1,
                        }}
                        onClick={handleNext}
                      >
                        {!isFallback && index + 1 < activeHorizontalStep?.index ? "✓  " : `${index + 1} .`}
                        {step.label}
                      </Typography>
                    );
                  })}
                </Box>
              )}

              <Divider
                sx={{
                  color: "rgba(215, 216, 222, 1)",
                  py: "5px",
                }}
              />
            </>
          ) : (
            <Grid
              container
              alignItems="flex-start"
              justifyContent="space-between"
              gap={0}
            >
              {/* Stepper (left side) */}
              <Grid item xs={12}>
                {isApplicant && (
                  <>
                    {/* Fallback: show step 1 as in-progress when data not yet loaded at (0,0) */}
                    {(!displayHorizontalSteps || displayHorizontalSteps.length === 0) &&
                      activeHorizontalIndex === 0 && (newactiveVerticalIndex === 0 || newactiveVerticalIndex === 1 || newactiveVerticalIndex === 2 || newactiveVerticalIndex === 3) &&
                      horizontalSteps && horizontalSteps.length > 0 && (
                        <Stepper
                          activeStep={0}
                          connector={<CustomConnector />}
                          sx={{ mb: 2 }}
                        >
                          {horizontalSteps.map((step: any, index: number) => (
                            <Step key={index} completed={false} active={index === 0}>
                              <StepLabel StepIconComponent={CustomStepIcon}>
                                <Typography
                                  fontSize="0.80rem"
                                  sx={{
                                    color: index === 0 ? "#00b3b5" : "#4B465C",
                                    fontWeight: index === 0 ? 600 : 500,
                                  }}
                                >
                                  {step.stepLabel}
                                </Typography>
                              </StepLabel>
                            </Step>
                          ))}
                        </Stepper>
                      )}
                    {displayHorizontalSteps && displayHorizontalSteps.length > 0 &&
                      <Stepper
                        activeStep={activeHorizontalIndex}
                        connector={<CustomConnector />}
                        sx={{ mb: 2 }}
                      >
                        {displayHorizontalSteps.map(
                          (step: any, index: number) => {
                            let isCompleted = step.isCompleted;
                            // A step is active only if it's the first non-completed step.
                            // If any previous step is incomplete, this step cannot be active.
                            const hasPrevIncompleteStep = displayHorizontalSteps
                              .slice(0, index)
                              .some((s: any) => !s.isCompleted);
                            let isActive = !isCompleted && !hasPrevIncompleteStep;
                            // Special case: at very first step (horizontal 0, vertical 0), force first step as in-progress
                            if (activeHorizontalIndex === 0 && newactiveVerticalIndex === 0 && index === 0) {
                              isActive = true;
                              isCompleted = false;
                            }
                            return (
                              <Step
                                key={index}
                                completed={isCompleted}
                                active={isActive}
                              >
                                <StepLabel StepIconComponent={CustomStepIcon}>
                                  <Typography
                                    fontSize="0.80rem"
                                    sx={{
                                      color:
                                        isActive || isCompleted
                                          ? "#00b3b5"
                                          : "#4B465C",
                                      fontWeight:
                                        isActive || isCompleted
                                          ? 600
                                          : 500,
                                    }}
                                  >
                                    {step.label}
                                  </Typography>
                                </StepLabel>
                              </Step>
                            );
                          }
                        )}
                      </Stepper>
                    }
                  </>)
                }
              </Grid>
            </Grid>
          )
          }

          {/* Tips Box */}
          {isAuthenticated && tipsArr && (
            <>
              {tipsArr
                .filter(
                  (tip) =>
                    tip.horizontalIndex === activeHorizontalIndex &&
                    tip.verticalIndex === newactiveVerticalIndex
                )
                .map((tip, index) => (
                  <Box mt={2} key={index}>
                    <TipsBox
                      heading="Tips"
                      headingone={tip.heading}
                      subheading={tip.subheading}
                      tip1={tip.tip1}
                      tip2={tip.tip2 || ""}
                      tip3={tip.tip3 || ""}
                      iconImage={isXsOrSm ? TipsIconMobile : TipsIcon}
                    />
                  </Box>
                ))}
            </>
          )}

          {/* {activeHorizontalIndex === 3 && <ViewSummary />} */}

          {/* {activeHorizontalIndex !== 3 && (
            <Box
              sx={{
                pb: !isXsOrSm
                  ? 0
                  : (activeHorizontalIndex === 0 &&
                    revisedActiveVerticalIndex === 3) ||
                    activeHorizontalIndex > 2
                    ? 0
                    : 10,
              }}
            >
              {activeHorizontalIndex === 2 ? (
                handleShowProvisionalOffer
              ) : (
                <Box mt={3}>{showVerticalStepper}</Box>
              )}
            </Box>
          )} */}
          <Box
              sx={{
                pb: !isXsOrSm
                  ? 0
                  : (activeHorizontalIndex === 0 &&
                      revisedActiveVerticalIndex === 3) ||
                    activeHorizontalIndex > 2
                  ? 0
                  : 10,
              }}
            >
              {activeHorizontalIndex === 2 ? (
                handleShowProvisionalOffer
              ) : (
                <Box mt={3}>{showVerticalStepper}</Box>
              )}
            </Box>

          {isAuthenticated && (
            <>
              {/* Navigation Buttons */}
              {isXsOrSm ? (
                <>
                  {(activeHorizontalIndex === 0 &&
                    revisedActiveVerticalIndex === 3) ||
                    activeHorizontalIndex > 2 ? (
                    <></>
                  ) : (
                    <Box
                    // sx={{
                    //   position: "fixed",
                    //   bottom: 0,
                    //   left: 0,
                    //   right: 0,
                    //   zIndex: 1000,
                    //   backgroundColor: "white",
                    //   boxShadow: "0px -2px 10px rgba(0, 0, 0, 0.1)",
                    //   mt: 4,
                    //   p: 2,
                    //   display: "flex",
                    //   justifyContent: "space-between",
                    //   alignItems: "center",
                    //   flexWrap: "wrap",
                    //   rowGap: 2,
                    // }}
                    >
                      <Box
                        display="flex"
                        flexWrap="wrap"
                        justifyContent="center"
                        alignItems="center"
                        sx={{ width: isXsOrSm ? "100%" : "auto" }}
                      >
                        {/* Previous button */}
                        {revisedActiveVerticalIndex === 0 ? (
                          <></>
                        ) : (
                          ""
                        )}

                        {/* Connect button */}
                        {/* {activeHorizontalIndex > 0 &&
                          trackingId &&
                          salesUserId && (
                            <Box>
                              <IconButton
                                ref={connectButtonRef}
                                onClick={handleChatConnectOpen}
                                sx={{
                                  width:
                                    activeHorizontalIndex > 1 ? "auto" : "100%",
                                }}
                              >
                                <img
                                  src={ChatIcon}
                                  alt="Chat"
                                  style={{
                                    width: 20,
                                    marginRight: !isXsOrSm ? 8 : 0,
                                  }}
                                />
                                {isXsOrSm && activeHorizontalIndex < 3
                                  ? ""
                                  : "Connect"}
                              </IconButton>
                            </Box>
                          )} */}

                        {/* Upload button */}
                        {/* {activeHorizontalIndex == 1 && (
                          <Box
                            sx={{
                              flex: { xs: 1, sm: "unset" },
                            }}
                          >
                            <OutlinedPrimaryButton
                              onClick={() => {
                                dispatch(setActiveHorizontalIndex(3));
                                navigate("/application/apply-now");
                              }}
                              sx={{ width: "100%" }}
                            >
                              <img
                                src={UploadIcon}
                                alt="Upload"
                                style={{ width: 20, marginRight: 8 }}
                              />
                              Upload Document
                            </OutlinedPrimaryButton>
                          </Box>
                        )} */}
                      </Box>

                      {/* {activeHorizontalIndex < 2 && (
                  <Box
                    sx={{
                      display: {
                        xs: "none", 
                        sm: "block", 
                      },
                    }}
                  >
                    <OutlinedPrimaryButton
                      onClick={handleSaveAsDraftAndNext}
                      sx={{ borderColor: "#00B3B5", color: "#00B3B5" }}
                    >
                      {activeStep === horizontalSteps.length - 1
                        ? "Finish"
                        : "Save as Draft"}
                    </OutlinedPrimaryButton>
                  </Box>
                )} */}
                    </Box>
                  )}
                </>
              ) : (
                <>
                  {(activeHorizontalIndex === 0 &&
                    revisedActiveVerticalIndex === 3) ||
                    activeHorizontalIndex > 2 ? (
                    <></>
                  ) : (
                    <Box
                    // sx={{
                    //   position: isPageScrollable ? "relative" : "fixed",
                    //   bottom: 0,
                    //   left: 0,
                    //   right: 0,
                    //   zIndex: 1000,
                    //   p: 2,
                    //   pl: isPageScrollable ? 0 : "100px",
                    //   mt: isPageScrollable ? 0 : 4,
                    //   display: "flex",
                    //   justifyContent: "space-between",
                    //   alignItems: "center",
                    //   flexWrap: "wrap",
                    //   rowGap: 2,
                    // }}
                    >
                      <Box
                        display="flex"
                        flexWrap="wrap"
                        justifyContent="center"
                        alignItems="center"
                        gap={2}
                        sx={{ width: isXsOrSm ? "100%" : "auto" }}
                      >
                        {/* Previous button */}
                        {revisedActiveVerticalIndex === 0 ? (
                          <>
                          </>
                        ) : (
                          ""
                        )}
                        {/* Connect button */}
                        {/* {activeHorizontalIndex > 0 &&
                          trackingId &&
                          salesUserId && (
                            <Box>
                              <IconButton
                                ref={connectButtonRef}
                                onClick={handleChatConnectOpen}
                                sx={{
                                  width:
                                    activeHorizontalIndex > 1 ? "auto" : "100%",
                                }}
                              >
                                <img
                                  src={ChatIcon}
                                  alt="Chat"
                                  style={{
                                    width: 20,
                                    marginRight: !isXsOrSm ? 8 : 0,
                                  }}
                                />
                                {isXsOrSm && activeHorizontalIndex < 3
                                  ? ""
                                  : "Connect"}
                              </IconButton>
                            </Box>
                          )} */}

                        {/* Upload button */}
                        {/* {activeHorizontalIndex == 1 && (
                          <Box
                            sx={{
                              flex: { xs: 1, sm: "unset" },
                            }}
                          >
                            <OutlinedPrimaryButton
                               onClick={() => {
                                dispatch(setActiveHorizontalIndex(3));
                                navigate("/application/apply-now");
                              }}
                              sx={{ width: "100%" }}
                            >
                              <img
                                src={UploadIcon}
                                alt="Upload"
                                style={{ width: 20, marginRight: 8 }}
                              />
                              Upload Document
                            </OutlinedPrimaryButton>
                          </Box>
                        )} */}
                      </Box>

                      {/* {activeHorizontalIndex < 2 && (
                  <Box
                    sx={{
                      display: {
                        xs: "none", 
                        sm: "block", 
                      },
                    }}
                  >
                    <OutlinedPrimaryButton
                      onClick={handleSaveAsDraftAndNext}
                      sx={{ borderColor: "#00B3B5", color: "#00B3B5" }}
                    >
                      {activeStep === horizontalSteps.length - 1
                        ? "Finish"
                        : "Save as Draft"}
                    </OutlinedPrimaryButton>
                  </Box>
                )} */}
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}

      {/* Chat Component */}
      {!shouldHideChatForPwa && isChatConnectOpen && (
        <>
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              zIndex: 1000,
              width: "100%",
              height: "100%",
            }}
            onClick={handleChatConnectClose}
          />

          <Box
            ref={chatRef}
            sx={{
              zIndex: 1301,
              position: "fixed",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-end",
              left: "auto",
              right: { xs: 16, md: 32 },
              width: { xs: "auto", md: "auto" },
              maxWidth: { xs: "calc(100% - 32px)", md: "400px" },
              bottom: {
                xs: 160,
                md: 120,
              },
            }}
          >
            <ConnectChatComponent
              userName={userProfile?.name || ""}
              open={isChatConnectOpen}
              onClose={handleChatConnectClose}
            />
          </Box>
        </>
      )}

      {/* Connect with sales floating button */}
      {!shouldHideChatForPwa && activeHorizontalIndex > 0 && activeHorizontalIndex < 3 && viewAccess && Object.keys(triggerData || {}).length === 0 && (
        <Box
          sx={{
            position: "fixed",
            bottom: { xs: 50, md: 32 },
            right: { xs: 16, md: 32 },
            zIndex: 1300,
          }}
        >
          <ButtonBase
            ref={connectButtonRef}
            onClick={handleChatConnectOpen}
            sx={{
              borderRadius: "50%",
              position: "relative",
              width: 96,
              height: 96,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "transform 0.2s ease-in-out",
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                backgroundColor: "#fecd3d",
                boxShadow: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2,
              }}
            >
              <Box
                component="img"
                src={ChatIcon}
                alt="Chat"
                sx={{
                  width: "80%",
                  height: "80%",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            </Box>

            <Box
              component="svg"
              viewBox="0 0 100 100"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
                zIndex: 1,
                overflow: "visible",
              }}
            >
              <path
                id="textCurve"
                d="M 8,50 A 42,42 0 0,0 92,50"
                fill="none"
              />

              <text
                fill="#333"
                fontSize="10"
                fontWeight="700"
                letterSpacing="0px"
                textAnchor="middle"
              >
                <textPath href="#textCurve" startOffset="50%">
                  Connect with sales
                </textPath>
              </text>
            </Box>
          </ButtonBase>
        </Box>
      )}
    </Box>
  );
};

export default HorizontalLinearStepper;
